import React, { useEffect, useRef, useState } from "react";
import { FolderLock, Upload, FileText, Folder } from "lucide-react";
import { useI18n } from "../../i18n";
import { api, apiError, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

export default function DocumentsRoom() {
  const { t } = useI18n();
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState("all");
  const [uploadFolder, setUploadFolder] = useState("Corporate");
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { api.get("/document-folders").then((r) => setFolders(r.data)); load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("folder", uploadFolder);
    try { await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("upload")); load(); }
    catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const filtered = active === "all" ? docs : docs.filter((d) => d.folder === active);
  const counts = folders.reduce((a, f) => ({ ...a, [f]: docs.filter((d) => d.folder === f).length }), {});

  return (
    <div className="space-y-5 gc-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)] flex items-center gap-2"><FolderLock className="h-6 w-6" /> {t("docroom_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("docroom_sub")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={uploadFolder} onValueChange={setUploadFolder}>
            <SelectTrigger data-testid="upload-folder-select" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{folders.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={fileRef} type="file" hidden onChange={upload} data-testid="docroom-file-input" />
          <Button data-testid="docroom-upload-btn" onClick={() => fileRef.current?.click()} className="rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]"><Upload className="h-4 w-4 me-1.5" /> {t("upload_document")}</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-5">
        <div className="space-y-1">
          <button data-testid="folder-all" onClick={() => setActive("all")} className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm ${active === "all" ? "bg-[hsl(217_63%_15%)] text-white" : "hover:bg-secondary"}`}>
            <span className="flex items-center gap-2"><Folder className="h-4 w-4" /> {t("all_folders")}</span><span className="font-mono text-xs">{docs.length}</span>
          </button>
          {folders.map((f) => (
            <button key={f} data-testid={`folder-${f}`} onClick={() => setActive(f)} className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm ${active === f ? "bg-[hsl(217_63%_15%)] text-white" : "hover:bg-secondary"}`}>
              <span className="flex items-center gap-2"><Folder className="h-4 w-4" /> {f}</span><span className="font-mono text-xs opacity-70">{counts[f] || 0}</span>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 space-y-2">
          {filtered.length === 0 && <div className="rounded-xl border border-border p-10 text-center text-muted-foreground text-sm">{t("no_documents")}</div>}
          {filtered.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center"><FileText className="h-4 w-4 text-[hsl(217_63%_15%)]" /></div>
                <div>
                  <div className="text-sm font-medium">{d.name}</div>
                  <div className="text-[10px] text-muted-foreground">{d.folder} · {(d.size / 1024).toFixed(0)} KB · {t("uploaded_by")} {d.uploaded_by_name}</div>
                </div>
              </div>
              <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-xs text-[hsl(158_64%_28%)] font-medium">{t("download")}</a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
